
/*
 * VNCmail : A whole new experience in enterprise email communication.
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */

import { Component, OnInit, NgZone, OnDestroy, OnChanges, ChangeDetectorRef } from "@angular/core";
import { MailBroadcaster } from "../../../../common/providers/mail-broadcaster.service";
import { BroadcastKeys } from "../../../../common/enums/broadcast.enum";
import { debounceTime, take, takeUntil } from "rxjs/operators";
import { MailRootState } from "src/app/mail/store";
import { Store } from "@ngrx/store";
import { getMailFolders } from "src/app/mail/store/selectors";
import { Subject } from "rxjs";
import { NgxHotkeysService } from "ngx-hotkeys-vnc";
import { MailFolder } from "src/app/mail/models/mail-folder.model";
import { NestedTreeControl } from "@angular/cdk/tree";
import { SearchRequest, Message } from "../../models";
import { MailService } from "../../services/mail-service";
import { ToastService } from "src/app/common/providers/toast.service";
import { ConversationRepository } from "src/app/mail/repositories/conversation.repository";
import { MailConstants } from "src/app/common/utils/mail-constants";
import { TranslateService } from "@ngx-translate/core";
import { MatTreeNestedDataSource } from "@angular/material/tree";
import { MatDialogRef } from "@angular/material/dialog";
import { FormControl } from "@angular/forms";
import { getBriefcaseFolders, getBriefcases } from "src/app/briefcase/store/selectors";
import { BriefcaseConstants } from "src/app/common/utils/briefcase-constants";
import { MailUtils } from "src/app/mail/utils/mail-utils";
import { ElectronService } from "src/app/services/electron.service";
import { BriefCase } from "src/app/briefcase/shared/models/briefcase.model";
import { ConfigService } from "src/app/config.service";
import { environment } from "src/environments/environment";
import { CommonUtils } from "src/app/common/utils/common-util";
import { MailTag } from "src/app/mail/models/mail-tag.model";
import { BriefcaseService } from "src/app/briefcase/services/briefcase.service";

@Component({
    selector: "vp-attach-briefcase-dialog",
    templateUrl: "./attach-briefcase-dialog.component.html"
})
export class AttachBriefcaseDialogComponent implements OnInit, OnDestroy {
    constructor(
        public dialogRef: MatDialogRef<AttachBriefcaseDialogComponent>,
        private mailBroadcaster: MailBroadcaster,
        private ngZone: NgZone,
        private appStore: Store<MailRootState>,
        private ngxHotKeyService: NgxHotkeysService,
        private mailService: MailService,
        private toastService: ToastService,
        private convRepository: ConversationRepository,
        private translate: TranslateService,
        private electronService: ElectronService,
        private configService: ConfigService,
        private briefcaseService: BriefcaseService,
        private changeDetectorRef: ChangeDetectorRef,
    ) {
        this.dialogRef.disableClose = true;
    }

    MailConstants = MailConstants;
    private isAlive$ = new Subject<boolean>();
    allFolders: MailFolder[] = [];
    offset: number = 0;
    limit: number = 50;
    selectedFolder: MailFolder;
    isMoreMessage: boolean = false;
    msgs: Message[] = [];
    selectedEmails: Message[] = [];
    searchField: string = "";
    treeControl = new NestedTreeControl<MailFolder>(node => node.children);
    dataSource = new MatTreeNestedDataSource<MailFolder>();
    currentTheme = localStorage.getItem("theme");
    searchControl: FormControl = new FormControl("", []);
    files: BriefCase[] = [];
    apiUrl = null;
    defaultColor = MailConstants.DEFAULT_COLOR;
    allZimbraTags: MailTag[] = [];
    hasChild = (_: number, node: MailFolder) => !!node.children && node.children.length > 0;

    ngOnInit() {
        this.apiUrl = this.configService.API_URL;
        this.searchControl.valueChanges
            .pipe(debounceTime(300))
            .subscribe((val: string) => {
            this.searchField = val;
            this.searchMessages();
            });
        this.ngxHotKeyService.pause(this.ngxHotKeyService.hotkeys);
        this.mailBroadcaster.on<any>(BroadcastKeys.HIDE_ATTACH_EMAIL_DIALOG).pipe(take(1)).subscribe(res => {
            this.ngZone.run(() => {
                this.close();
            });
        });
        this.appStore.select(getBriefcaseFolders).pipe(take(1)).subscribe(res => {
            console.log("getBriefcaseFolders response", res);
            if (res) {
                this.allFolders = res;
                this.dataSource.data = this.allFolders.filter( f => f.absFolderPath !== "/starred");
                this.treeControl.dataNodes = this.allFolders;
                this.treeControl.expandAll();
                this.allFolders.forEach(folder => this.fixFolderPaths(folder));
                let folderAbsPath = "/Briefcase";
                const childFolders = MailUtils.getChildFolders(res);
                const allFolders = [...res, ...childFolders];
                if (!!folderAbsPath && folderAbsPath !== "") {
                    allFolders.map(f => {
                        if (f.absFolderPath === folderAbsPath) {
                            this.selectedFolder = f;
                            this.getMessages(this.selectedFolder);
                        }
                    });
                }
            }
        });
    }

    getFileIcon(contentType: string): any {
        if (contentType === "image/bmp" || contentType === "image/gif") {
        return { icon: "mdi-image", isImage: true };
        } else {
        return MailUtils.getFileAndIcon(contentType);
        }
    }

    ngOnDestroy(): void {
        this.isAlive$.next(false);
        this.isAlive$.unsubscribe();
        this.ngxHotKeyService.unpause(this.ngxHotKeyService.hotkeys);
    }

    close(ids?: any) {
        this.dialogRef.close({ ids: ids });
    }

    folderItem(node: MailFolder): void {
        this.offset = 0;
        this.limit = 50;
        this.selectedFolder = node;
        this.getMessages(node);
    }

    fixFolderPaths(folder, parentPath: string = "") {
        if (!folder.absFolderPath) {
            folder.absFolderPath = parentPath ? `${parentPath}/${folder?.name}` : `/${folder.name}`;
        }

        if (folder.children?.length) {
            for (const child of folder.children) {
                this.fixFolderPaths(child, folder.absFolderPath);
            }
        }
    }

    loadPreviousMail(): void {
        this.offset -= this.limit;
        this.getMessages(this.selectedFolder);
    }

    loadNextMail(): void {
        this.offset += this.limit;
        this.getMessages(this.selectedFolder);
    }

    getMessages(node: MailFolder): void {
        this.selectedEmails = [];
        if (node !== null) {
            if (node.id.indexOf(":") !== -1) {
                this.searchField = "in:" + "\"" + node.shareFolderSearchPath.substr(1) + "\"";
            } else {
                this.searchField = "in:" + "\"" + node.absFolderPath.substr(1) + "\"";
            }
            const query = "inid:" + "\"" + node.id + "\"";
            this.getAllMessages(query);
        } else {
            this.getAllMessages(this.searchField);
        }
    }
    getTagColor(tagsName: string): string {
        let color = this.defaultColor;
        if (tagsName) {
        const tags = tagsName.split(",");
        const t = this.allZimbraTags.filter(tt => tt.name.toLowerCase() === tags[0].toLowerCase())[0];
        if (!!t) {
            if (t.color && MailConstants.COLOR_LIST[t.color]) {
            t.color = MailConstants.COLOR_LIST[t.color];
            }
            if (t.rgb) {
            t.rgb = t.rgb.toLowerCase();
            if (t.rgb === "#999") {
                t.rgb = "#999999";
            }
            }
            color = t.rgb || t.color || this.defaultColor;
        }
        }
        return color;
    }

    getAllMessages(query: string): void {
        const searchQuery: SearchRequest = {
            fullConversation: 1,
            limit: this.limit,
            needExp: 1,
            offset: this.offset,
            query: query,
            types: "document"
        };
        this.mailService.searchRequest(searchQuery).pipe(take(1)).subscribe(file => {
            if (file.doc) {
                this.files = file.doc;
                this.prepareFile();
            } else {
                this.files = [];
            }
            if (!file.more) {
                this.isMoreMessage = true;
            } else {
                this.isMoreMessage = false;
            }
            if (this.offset < 50) {
                if (file.doc) {
                    this.limit = file.doc.length;
                } else {
                    this.limit = 0;
                    this.offset = 0;
                }
            }
        }, error => {
            this.toastService.showPlainMessage(error);
        });
    }

    getAvatar(email) {
        return this.convRepository.getAvatar(email);
    }

    getUserId(): string {
        return JSON.parse(localStorage.sectionAttributes).id;
    }

    isRevisionFile(file: any): boolean {
        if (file.id.indexOf("_") !== -1) {
            return true;
        }
        return false;
    }

    selectEmail(file, index: number, $event: MouseEvent): void {
        $event.preventDefault();
        if (this.selectedEmails.indexOf(file) === -1) {
            if ($event.ctrlKey) {
                this.selectedEmails.push(file);
            } else {
                this.selectedEmails = [];
                this.selectedEmails.push(file);
            }
        } else {
            if (this.selectedEmails.length > 1) {
                this.selectedEmails.splice(this.selectedEmails.indexOf(file), 1);
            }
        }
    }


    isSelectedEmailMessage(email: any): boolean {
        return this.selectedEmails.indexOf(email) !== -1;
    }

    attachMessages(): void {
        let fileIds;
        this.selectedEmails.forEach(file => {
            let version;
            if (this.isRevisionFile(file)) {
                version = file.id.split("_")[1];
                file.id = file.id.split("_")[0];
            }
            const id = this.getUserId();
            fileIds = {
                id : id + ":" + file.id
            };
            if (version) {
                fileIds["ver"] = Number(version);
            }
        });
        this.close(fileIds);
    }

    searchMessages(): void {
        this.offset = 0;
        this.limit = 50;
        this.selectedFolder = null;
        this.getAllMessages(this.searchField);
    }
      prepareUrl(url) {
        return (environment.isCordova || environment.isElectron) ? CommonUtils.addTokenToRequest(url, true) : url;
      }

      prepareFile() {
        let oldExpandedFileIds = [];
        let oldRevisions = {};
        if (this.files && (this.files.length > 0)) {
          const expandedFiles = this.files.filter((f: any) => f.isExpand);
          if (expandedFiles && (expandedFiles.length > 0)) {
            oldExpandedFileIds = expandedFiles.map(f => f.id);
          }
          expandedFiles.forEach((f: any) => {
            oldRevisions[f.id] = f.revisions;
          });
          // console.log("getBriefcaseSub old expanded: ", expandedFiles, oldExpandedFileIds);
        }
        // console.log("getBriefcaseSub oldfiles, res: ", oldExpandedFileIds, res);
        this.files = this.files.map((file: any) => {
          file.type = file.ct.split("/")[0];
          if (file.type === "image") {
            file.url = `${this.configService.API_URL}/api/getAttachment?id=${file.id}`;
          }
          file.cr = file.cr.split("@")[0];
          file.icon = this.getFileIcon(file.ct);
          file.isExpand = (oldExpandedFileIds.indexOf(file.id) > -1) ? true : false;
          file.revisions = (oldExpandedFileIds.indexOf(file.id) > -1) ? oldRevisions[file.id] : [];
          return file;
        });
    }

    toggleItem(file: any, isExpand: boolean): void {
        file.isExpand = isExpand;
        if (isExpand) {
        const doc = {
            count: 50,
            id: file.id,
            ver: -1
        };
        const request = {
            ListDocumentRevisionsRequest: {
            "@": {
                xmlns: "urn:zimbraMail"
            },
            _jsns: "urn:zimbraMail",
            doc: doc
            }
        };
        this.briefcaseService.createBatchRequest(request).subscribe((response: any) => {
            if (response.ListDocumentRevisionsResponse && response.ListDocumentRevisionsResponse[0].doc) {
            const revision = response.ListDocumentRevisionsResponse[0].doc;
            revision.map( f => {
                f.type = f.ct.split("/")[0];
                if (f.type === "image") {
                f.url = `${this.apiUrl}/api/getAttachment?id=${f.id}`;
                }
                f.cr = f.cr.split("@")[0];
                f.icon = this.getFileIcon(f.ct);
                f.id = `${f.id}_${f.ver}`;
            });
            file.revisions = revision;
            this.changeDetectorRef.markForCheck();
            }
        });
        this.changeDetectorRef.markForCheck();
        }
    }
}


